/**
 * Language carried by the URL.
 *
 * French pages keep their current URLs (/scooters, /services/…); the English
 * version of a page lives under the /en prefix (/en/scooters, /en/services/…).
 * The router runs with `basename` "/en" on English URLs, so every <Link> and
 * navigate() of the app keeps the prefix without being changed.
 *
 * One language per public URL: /en/… always shows English, every other
 * public URL always shows French, whatever the browser's language or the
 * saved choice (search engines see the same language as visitors, matching
 * the HTML lang, canonical and hreflang). Private pages (dashboard, sign-in…)
 * are not forced: they keep the language of the session, else the saved
 * choice (LanguageSwitcher), else the browser's language. Visitors are never
 * redirected according to their browser's language.
 *
 * Only the static pages listed in publishedPages.json exist in English for
 * search engines (own canonical, hreflang fr/en/x-default). Every other /en
 * page (listings, account pages…) is served with noindex and a canonical to
 * its French URL (SEO.jsx and the Cloudflare Function functions/en/[[path]].js).
 */
import published from './publishedPages.json';

export const SITE_URL = 'https://ridalo.co';
export const EN_PREFIX = '/en';
export const PUBLISHED_EN_PATHS = published.en;
// Pages with their own English slug: French app path → English app path.
export const LOCALIZED_PATHS = published.localized || {};
const FR_FOR_EN = Object.fromEntries(Object.entries(LOCALIZED_PATHS).map(([fr, en]) => [en, fr]));

/** App path of a page in a language ("/location-scooter-patong" ↔ "/scooter-rental-patong"). */
export const pathInLang = (lang, appPath) => {
  const fr = FR_FOR_EN[appPath] || appPath;
  return lang === 'en' ? (LOCALIZED_PATHS[fr] || fr) : fr;
};

/** "en" when the path is under the /en prefix, else null. */
export const langFromPath = (pathname) => {
  const p = String(pathname || '');
  return p === EN_PREFIX || p.startsWith(`${EN_PREFIX}/`) ? 'en' : null;
};

/** React Router basename for this page load: "/en" or "" (French URLs). */
export const routerBasename = (pathname) => (langFromPath(pathname) ? EN_PREFIX : '');

/** Path inside the app, without the language prefix ("/en/faq" → "/faq"). */
export const stripLangPrefix = (pathname) => {
  if (!langFromPath(pathname)) return pathname || '/';
  return pathname.slice(EN_PREFIX.length) || '/';
};

/**
 * Browser URL of an app path in a language: "/faq" → "/faq" (fr) or
 * "/en/faq" (en); the home page is "/" or "/en".
 */
export const pathForLang = (lang, appPath = '/', search = '', hash = '') => {
  const path = pathInLang(lang, stripLangPrefix(appPath.startsWith('/') ? appPath : `/${appPath}`));
  const prefixed = lang === 'en' ? (path === '/' ? EN_PREFIX : `${EN_PREFIX}${path}`) : path;
  return `${prefixed}${search || ''}${hash || ''}`;
};

/** Private pages (not indexed, robots.txt): their language is never forced by the URL. */
const PRIVATE_PATHS = ['/dashboard', '/login', '/register', '/forgot-password', '/reset-password', '/google-callback', '/bookings', '/account'];

/** True for a private page path (with or without the /en prefix). */
export const isPrivatePath = (pathname) => {
  const p = stripLangPrefix(pathname || '/');
  return PRIVATE_PATHS.some((x) => p === x || p.startsWith(`${x}/`));
};

/**
 * Language imposed by a URL: "en" under /en, "fr" for a French public page,
 * null for a French private page (and the Google sign-in return, which may
 * land on any path with #session_id) — those keep the session's language.
 */
export const routeLanguage = (pathname, hash = '') => {
  if (langFromPath(pathname)) return 'en';
  if (isPrivatePath(pathname) || String(hash || '').includes('session_id=')) return null;
  return 'fr';
};

const SAVED_KEY = 'ridalo_lang';            // explicit choice (LanguageSwitcher), as before
const SESSION_KEY = 'ridalo:sessionLang';   // language shown last in this tab
const read = (store, key) => {
  try {
    const v = store.getItem(key);
    return v === 'fr' || v === 'en' ? v : null;
  } catch (e) {
    return null;
  }
};
const write = (store, key, lang) => {
  try { store.setItem(key, lang); } catch (e) { /* private mode: nothing kept */ }
};

/** Language last shown in this tab (kept across the Google sign-in round trip). */
export const sessionLanguage = () => (typeof window === 'undefined' ? null : read(window.sessionStorage, SESSION_KEY));
export const rememberSessionLanguage = (lang) => {
  if ((lang === 'fr' || lang === 'en') && typeof window !== 'undefined') write(window.sessionStorage, SESSION_KEY, lang);
};
/** Explicit choice from the FR/EN button: used by private pages on later visits. */
export const rememberChosenLanguage = (lang) => {
  if (typeof window === 'undefined') return;
  write(window.localStorage, SAVED_KEY, lang);
  rememberSessionLanguage(lang);
};

/** Language at page load: the URL's, else (private page) this tab's, else the detector's (saved choice, browser). */
export const initialLanguage = () => (typeof window === 'undefined' ? undefined
  : (routeLanguage(window.location.pathname, window.location.hash) || sessionLanguage() || undefined));

/** Language of the current page URL ("en" or null). */
export const currentUrlLang = () => (typeof window === 'undefined' ? null : langFromPath(window.location.pathname));

/**
 * Raw href for a plain <a> or window.open() (outside the router): the app
 * path with the current page's language prefix.
 */
export const localizedHref = (appPath) => pathForLang(currentUrlLang() || 'fr', appPath);

/** App path without trailing slash ("/faq/" → "/faq", "" → "/"). */
const cleanPath = (appPath) => (String(appPath || '/').replace(/\/+$/, '') || '/');

/** True when the static page at `appPath` is published in English. */
export const isPublishedInEnglish = (appPath) => {
  const fr = pathInLang('fr', cleanPath(stripLangPrefix(appPath)));
  return PUBLISHED_EN_PATHS.includes(fr) || Boolean(LOCALIZED_PATHS[fr]);
};

/** Absolute URL of an app path in a language ("/" → https://ridalo.co/ or https://ridalo.co/en). */
export const absoluteUrl = (lang, appPath) => {
  const p = pathInLang(lang, cleanPath(stripLangPrefix(appPath)));
  return lang === 'en' ? `${SITE_URL}${EN_PREFIX}${p === '/' ? '' : p}` : `${SITE_URL}${p}`;
};

/** hreflang links of a published page: fr, en and x-default (the French URL). */
export const hreflangLinks = (appPath) => (isPublishedInEnglish(appPath) ? [
  { hreflang: 'fr', href: absoluteUrl('fr', appPath) },
  { hreflang: 'en', href: absoluteUrl('en', appPath) },
  { hreflang: 'x-default', href: absoluteUrl('fr', appPath) },
] : []);

/** Browser URL to open when switching to `lang` from the current page
 *  (`location` is the router location, without the /en prefix); null when
 *  the language changes in place (French private page: dashboard, sign-in…). */
export const switchTarget = (lang, location, urlLang) => {
  if (!urlLang && isPrivatePath(location.pathname)) return null;
  const target = pathForLang(lang, location.pathname, location.search, location.hash);
  const current = pathForLang(urlLang || 'fr', location.pathname, location.search, location.hash);
  return target === current ? null : target;
};
